// taken and adapted from https://github.com/jepser/use-match-media

import { useCallback, useMemo, useSyncExternalStore } from 'react';

/**
 * `useBreakpoint` is a hook used to determine if the document matches the media
 * query string (or media query list). It will listen to the breakpoints specified
 * and will return a boolean value indicating if this media query is met or not.
 * @public
 */
function useBreakpoint(
  /** Media Query you want to test, eg '(min-width: 800px)' */
  query: string,
  /** Default value (fallback value to support SSR)
   * @defaultValue false
   */
  ssrInitialValue?: boolean,
): boolean;

/**
 * `useBreakpoint` is a hook used to determine if the document matches the media
 * query string (or media query list). It will listen to the breakpoints specified
 * and will return a boolean value indicating if this media query is met or not.
 * @public
 */
function useBreakpoint(
  /** Media Queries you want to test, eg '[(min-width: 400px), (min-width: 800px)]' */
  queries: string[],
  /** Default values (fallback values to support SSR)
   * @defaultValue []
   */
  ssrInitialValue?: boolean[],
): boolean[];

/** @public */
function useBreakpoint(
  queries: string | string[],
  ssrInitialValue?: boolean | boolean[],
): boolean | boolean[] {
  const _ssrInitialValue =
    ssrInitialValue ?? (Array.isArray(queries) ? [] : false);

  const mediaQueryLists = useMemo(() => {
    if (typeof window === 'undefined') {
      return [];
    }
    const queryStrings = Array.isArray(queries) ? queries : [queries];
    return queryStrings.map((q) => window.matchMedia(q));
  }, [queries]);

  const subscribe = useCallback(
    (callback: () => void) => {
      mediaQueryLists.forEach((mql) =>
        mql.addEventListener('change', callback),
      );
      return (): void => {
        mediaQueryLists.forEach((mql) =>
          mql.removeEventListener('change', callback),
        );
      };
    },
    [mediaQueryLists],
  );

  /**
   * Generates an immutable snapshot of the current media query matches.
   * This is necessary to ensure the snapshot remains consistent and does not change.
   * @see {@link https://react.dev/reference/react/useSyncExternalStore#im-getting-an-error-the-result-of-getsnapshot-should-be-cached}
   */
  const getSnapshot = () => {
    return JSON.stringify(mediaQueryLists.map((q) => q.matches));
  };

  const getServerSnapshot = () => {
    if (Array.isArray(_ssrInitialValue)) {
      // Ensure the array is filled with false values or sliced to match queries.length
      const adjustedValue = _ssrInitialValue
        .concat(Array(queries.length).fill(false))
        .slice(0, queries.length);
      return JSON.stringify(adjustedValue);
    }
    return JSON.stringify([_ssrInitialValue]);
  };

  const queryValues = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  if (!Array.isArray(queries)) {
    return JSON.parse(queryValues)[0];
  }
  return JSON.parse(queryValues);
}

export { useBreakpoint };
